import { Document, FilePdf, CheckCircle, XCircle, AlertErrorCircle, WarningTriangle } from '@/assets/icons'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import clsx from 'clsx'

export enum MedChronFileStatus {
  UPLOADING = 'uploading',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  EXTRACTED = 'extracted',
  ERROR = 'error',
}

type MedChronFileCardProps =
  // EXTRACTED
  | {
      fileName: string
      status: MedChronFileStatus.EXTRACTED
      numEvents: number // Required for displaying events extracted count
      errorMessage?: never
      onRemove?: never
      onClick?: () => void
    }
  // All other statuses
  | {
      fileName: string
      status: Exclude<MedChronFileStatus, MedChronFileStatus.EXTRACTED>
      numEvents?: never
      errorMessage?: string
      onRemove: () => void
      onClick?: () => void
    }

export default function MedChronFileCard(props: MedChronFileCardProps) {
  const { fileName, status, errorMessage, numEvents, onClick } = props

  // Determine which icon to use based on file extension
  const FileIcon = fileName.toLowerCase().endsWith('.pdf') ? FilePdf : Document

  const noEventsExtracted = status === MedChronFileStatus.EXTRACTED && numEvents === 0

  // Allow clicking when onClick handler exists, status is one of PROCESSING / COMPLETED / EXTRACTED, and is a PDF file (only supported type for Med Chron)
  const isClickable =
    onClick &&
    [MedChronFileStatus.PROCESSING, MedChronFileStatus.COMPLETED, MedChronFileStatus.EXTRACTED].includes(status) &&
    fileName.toLowerCase().endsWith('.pdf')

  // Generate text for events extracted count (only when status is EXTRACTED)
  const getEventsText = (count: number): string => {
    if (count === 0) return 'No events extracted'
    if (count === 1) return '1 event extracted'
    return `${count} events extracted`
  }

  // Render the left icon section based on status
  const renderLeftSection = () => {
    switch (status) {
      case MedChronFileStatus.UPLOADING:
      case MedChronFileStatus.PROCESSING:
        return (
          <div className="w-[60px] h-full flex items-center justify-center px-3 border-r rounded-l-[5px]">
            <CircularProgressContinuousSized size={24} thickness={3} showTrack={true} />
          </div>
        )
      case MedChronFileStatus.COMPLETED:
        return (
          <div className="w-[60px] h-full flex items-center justify-center px-3 border-r rounded-l-[5px]">
            <FileIcon className="w-6 h-6 text-brand-neutral-800" />
          </div>
        )
      case MedChronFileStatus.EXTRACTED:
        return (
          <div
            className={clsx('w-[60px] h-full flex items-center justify-center px-3 border-r rounded-l-[5px]', noEventsExtracted && 'border-brand-neutral-300')}
          >
            <FileIcon className="w-6 h-6 text-brand-neutral-800" />
          </div>
        )
      case MedChronFileStatus.ERROR:
        // Show document icon with error indicator - status details shown in renderStatus()
        return (
          <div className="w-[60px] h-full flex items-center justify-center px-3 border-r rounded-l-[5px] bg-red-50">
            <FileIcon className="w-6 h-6 text-red-600" />
          </div>
        )
      default:
        return null
    }
  }

  // Render status text and icon
  const renderStatus = () => {
    switch (status) {
      case MedChronFileStatus.UPLOADING:
        return <p className="text-xs font-medium italic text-brand-neutral-600">Uploading...</p>
      case MedChronFileStatus.PROCESSING:
        return <p className="text-xs font-medium italic text-brand-neutral-600">Processing...</p>
      case MedChronFileStatus.COMPLETED:
        return (
          <div className="flex items-center gap-1 text-green-800">
            <CheckCircle className="w-3.5 h-3.5" />
            <p className="text-xs font-medium">Completed</p>
          </div>
        )
      case MedChronFileStatus.EXTRACTED:
        return (
          <div className={clsx('flex items-center gap-1', noEventsExtracted ? 'text-yellow-800' : 'text-green-800')}>
            {noEventsExtracted ? <WarningTriangle className="w-3.5 h-3.5" /> : <CheckCircle className="w-3.5 h-3.5" />}
            <p className="text-xs font-medium">{getEventsText(numEvents)}</p>
          </div>
        )
      case MedChronFileStatus.ERROR:
        return (
          <div className="flex items-center gap-1">
            <AlertErrorCircle className="w-3.5 h-3.5 text-red-800" />
            <p className="text-xs font-medium text-red-800">{errorMessage || 'Document could not be read'}</p>
          </div>
        )
      default:
        return null
    }
  }

  return (
    <div
      className={clsx(
        'h-[60px] flex items-center gap-[10px] rounded-[5px] border relative',
        noEventsExtracted ? 'bg-yellow-50 border-brand-neutral-300' : 'bg-white',
        isClickable && 'cursor-pointer hover:bg-brand-neutral-100'
      )}
      onClick={isClickable ? onClick : undefined}
      tabIndex={isClickable ? 0 : undefined}
      role={isClickable ? 'button' : undefined}
      onKeyDown={(e) => {
        if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          onClick?.()
        }
      }}
    >
      {/* Left icon section */}
      {renderLeftSection()}

      {/* Main content area */}
      <div className="flex-1 flex flex-col gap-1 justify-center pr-12 min-w-0">
        <p className="text-[13px] font-medium text-brand-neutral-950 truncate">{fileName}</p>
        {renderStatus()}
      </div>

      {/* Remove button */}
      {status !== MedChronFileStatus.EXTRACTED && props.onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            props.onRemove()
          }}
          onKeyDown={(e) => {
            if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault()
              e.stopPropagation()
              props.onRemove()
            }
          }}
          className="absolute right-1 top-1/2 -translate-y-1/2 p-2 rounded-md hover:bg-brand-neutral-50 transition-colors duration-200"
          aria-label={`Remove ${fileName}`}
        >
          <XCircle className="w-5 h-5 text-brand-neutral-600 hover:text-brand-neutral-800" />
        </button>
      )}
    </div>
  )
}
